"use client";

import type { ReactNode } from "react";
import { Link, NavLink, Text, type TextProps } from "@/components/ui";
import { PageTypePortal } from "@/lib/types";
import { useThemeTokens } from "@/lib/theme/tokens";
import {
  CONTENT_SETTINGS_NO_CONTACT_FALLBACK,
  interpolateContentSettingsString,
  parseContentSettingsTemplate,
  resolveTenantEmail,
  type ContentSettingsPlaceholderContext,
} from "./interpolate-content-settings";

export type { ContentSettingsPlaceholderContext };
export {
  CONTENT_SETTINGS_NO_CONTACT_FALLBACK,
  interpolateContentSettingsString,
  resolveTenantEmail,
};

type ContentSettingsTextProps = Omit<TextProps, "children"> & {
  /** Template string from content settings. */
  children: string;
  studioName?: string | null;
  tenantEmail?: string | null;
  /** Replaces {userEmail} in template (e.g. forgot-password success copy). */
  userEmail?: string | null;
  /** Replaces {className} in template (not the HTML class attribute). */
  classLabel?: string | null;
  /** Replaces {seasonName} in template. */
  seasonName?: string | null;
  /** When {email} is present but tenant has no contact email. */
  fallbackWithoutContact?: string;
};

/**
 * Renders microsite copy with optional {studioName}, {email}, {userEmail}, {className} placeholders.
 * {email} becomes a mailto link when a real tenant email exists.
 */
export function ContentSettingsText({
  children: template,
  studioName,
  tenantEmail,
  userEmail,
  classLabel: classNamePlaceholder,
  seasonName,
  fallbackWithoutContact = CONTENT_SETTINGS_NO_CONTACT_FALLBACK,
  ...textProps
}: ContentSettingsTextProps) {
  const theme = useThemeTokens();

  if (template.includes("{signUp}")) {
    const studio = studioName?.trim() || "us";
    const classLabel = classNamePlaceholder?.trim() || "this class";
    const seasonLabel = seasonName?.trim() || "this season";
    let processed = template
      .replace(/\{studioName\}/g, studio)
      .replace(/\{className\}/g, classLabel)
      .replace(/\{seasonName\}/g, seasonLabel);

    const signUpSegments = processed.split("{signUp}");
    const signUpNodes: ReactNode[] = [];
    signUpSegments.forEach((part, index) => {
      if (part) signUpNodes.push(<span key={`su-part-${index}`}>{part}</span>);
      if (index < signUpSegments.length - 1) {
        signUpNodes.push(
          <Link
            key={`su-link-${index}`}
            as={NavLink}
            href={PageTypePortal.SignUp}
            fontWeight={500}
            color={theme.accent.primary}
          >
            sign-up
          </Link>,
        );
      }
    });
    return <Text {...textProps}>{signUpNodes}</Text>;
  }

  const parsed = parseContentSettingsTemplate(
    template,
    {
      studioName,
      tenantEmail,
      userEmail,
      className: classNamePlaceholder,
      seasonName,
    },
    { fallbackWithoutContact },
  );

  if (parsed.kind === "fallback") {
    return <Text {...textProps}>{parsed.text}</Text>;
  }

  const nodes: ReactNode[] = parsed.segments.map((segment, index) => {
    if (segment.type === "text") {
      return <span key={`part-${index}`}>{segment.value}</span>;
    }

    return (
      <Link
        key={`email-${index}`}
        href={segment.href}
        color={theme.accent.primary}
        fontWeight={500}
        sx={{
          "@media (hover: hover)": {
            "&:hover": { textDecoration: "underline" },
          },
        }}
      >
        {segment.value}
      </Link>
    );
  });

  return <Text {...textProps}>{nodes}</Text>;
}
